/**
 * useAnalytics Hook - Manages analytics lifecycle and provides tracking functions.
 *
 * - Automatically starts/ends sessions tied to authentication state.
 * - Tracks page views on route changes.
 * - Exposes convenience methods for tracking quiz/question events.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { analyticsService } from '@shared/services/analyticsService';

interface UseAnalyticsOptions {
  /** Whether the user is authenticated. Analytics only track for authenticated users. */
  isAuthenticated: boolean;
}

interface UseAnalyticsResult {
  /** Track a question answered event. */
  trackQuestionAnswered: (data: {
    topicId: string;
    questionIndex: number;
    isCorrect: boolean;
    responseTimeSeconds: number;
  }) => void;

  /** Track a quiz started event. */
  trackQuizStarted: (data: { topicId: string; attemptId: string }) => void;

  /** Track a quiz completed event. */
  trackQuizCompleted: (data: {
    topicId: string;
    attemptId: string;
    score: number;
    totalQuestions: number;
    correctAnswers: number;
    timeSpentSeconds: number;
  }) => void;
}

/**
 * Hook for analytics tracking. Must be used within a Router context.
 */
export const useAnalytics = ({ isAuthenticated }: UseAnalyticsOptions): UseAnalyticsResult => {
  const location = useLocation();
  const sessionStartedRef = useRef(false);
  const prevPathnameRef = useRef<string>('');

  // Initialize and tear down analytics service
  useEffect(() => {
    analyticsService.init();

    return () => {
      analyticsService.destroy();
    };
  }, []);

  // Start/end session based on auth state
  useEffect(() => {
    if (isAuthenticated && !sessionStartedRef.current) {
      analyticsService.trackSessionStart();
      sessionStartedRef.current = true;
    } else if (!isAuthenticated && sessionStartedRef.current) {
      analyticsService.trackSessionEnd();
      analyticsService.reset();
      sessionStartedRef.current = false;
    }
  }, [isAuthenticated]);

  // Track page views on route changes
  useEffect(() => {
    if (isAuthenticated && location.pathname !== prevPathnameRef.current) {
      prevPathnameRef.current = location.pathname;
      analyticsService.trackPageView(location.pathname);
    }
  }, [location.pathname, isAuthenticated]);

  const trackQuestionAnswered = useCallback(
    (data: {
      topicId: string;
      questionIndex: number;
      isCorrect: boolean;
      responseTimeSeconds: number;
    }) => {
      if (isAuthenticated) {
        analyticsService.trackQuestionAnswered(data);
      }
    },
    [isAuthenticated]
  );

  const trackQuizStarted = useCallback(
    (data: { topicId: string; attemptId: string }) => {
      if (isAuthenticated) {
        analyticsService.trackQuizStarted(data);
      }
    },
    [isAuthenticated]
  );

  const trackQuizCompleted = useCallback(
    (data: {
      topicId: string;
      attemptId: string;
      score: number;
      totalQuestions: number;
      correctAnswers: number;
      timeSpentSeconds: number;
    }) => {
      if (isAuthenticated) {
        analyticsService.trackQuizCompleted(data);
      }
    },
    [isAuthenticated]
  );

  return {
    trackQuestionAnswered,
    trackQuizStarted,
    trackQuizCompleted,
  };
};
